/* eslint-disable no-param-reassign */
// this is what @reduxjs/toolkit expects
import dayjs from 'dayjs'
import {
  createReducer,
  createSelector,
  createAsyncThunk,
} from '@reduxjs/toolkit'
import { redFunnelAPI } from '../api/api'
import { logout } from './authentication'
import { cancelledCodes } from '../config/itineraryStatusCodes'
import getErrorMessage from '../utilities/getErrorMessage'

export const fetchItineraries = createAsyncThunk('itineraries/fetch', args => {
  return redFunnelAPI.getItineraries({
    pageNumber: args?.pageNumber ?? null,
  })
})

export const checkForUnresolvedItineraries = createAsyncThunk(
  'unresolvedItineraries/fetch',
  () => {
    return redFunnelAPI
      .getUnResolvedItineraries()
      .then(res => Object.values(res))
  },
)

export const fetchItinerary = createAsyncThunk(
  'itinerary/fetch',
  (id, thunkAPI) => {
    const currentItineraryState =
      thunkAPI.getState()?.itineraries?.data?.[id] || {}
    return redFunnelAPI
      .getItinerary(id)
      .then(res => ({
        ...currentItineraryState,
        ...(res.data || {}),
      }))
      .catch(err => {
        const message = getErrorMessage(err, 'Unauthorized itinerary')
        throw new Error(message)
      })
  },
)

const initialState = {
  loading: true,
  data: {},
  error: null,
  isLoadingUnResolvedItineraries: false,
  unResolvedItineraries: [],
  unResolvedItinerariesError: null,
}

export const itinerariesReducer = createReducer(initialState, builder => {
  builder
    .addCase(fetchItineraries.pending, state => {
      state.loading = true
      state.error = null
    })
    .addCase(fetchItineraries.fulfilled, (state, action) => {
      state.loading = false
      const currentData = state.data || {}
      state.data = {
        ...currentData,
        ...(action.payload.data || {}),
      }
    })
    .addCase(fetchItineraries.rejected, (state, action) => {
      state.loading = false
      state.data = null
      state.error = action.payload
    })
    .addCase(fetchItinerary.pending, (state, action) => {
      const id = action?.meta?.arg
      state.data[id] = {
        ...state.data[id],
        loading: true,
      }
    })
    .addCase(fetchItinerary.fulfilled, (state, action) => {
      state.data[action.payload.id] = {
        ...action?.payload,
        loading: false,
      }
    })
    .addCase(fetchItinerary.rejected, (state, action) => {
      const errorMessage = action?.error?.message || 'unable to fetch itinerary'
      const id = action?.meta?.arg
      if (state.data?.[id]) {
        state.data[id].error = errorMessage
        state.data[id].loading = false
      }
    })
    .addCase(logout.fulfilled, state => {
      state.loading = false
      state.data = null
      state.error = null
    })
  // .addCase(checkForUnresolvedItineraries.pending, state => {
  //   state.unResolvedItineraries = []
  //   state.isLoadingUnResolvedItineraries = true
  //   state.unResolvedItinerariesError = null
  // })
  // .addCase(checkForUnresolvedItineraries.fulfilled, (state, action) => {
  //   state.unResolvedItineraries = action?.payload || []
  //   state.isLoadingUnResolvedItineraries = false
  //   state.unResolvedItinerariesError = null
  // })
  // .addCase(checkForUnresolvedItineraries.rejected, (state, action) => {
  //   state.isLoadingUnResolvedItineraries = false

  //   state.unResolvedItinerariesError =
  //     action?.error?.response?.data?.['hydra:description'] ||
  //     action?.error?.message ||
  //     'unable to fetch'
  // })
})

const selectState = state => state.itineraries

export const selectItineraryState = createSelector(
  selectState,
  itinerariesState => itinerariesState,
)

export const selectItineraries = createSelector(
  selectItineraryState,
  itinerariesState => {
    const pending = (itinerariesState.unResolvedItineraries || []).filter(
      pendingId => !itinerariesState?.data?.[pendingId],
    )
    return Object.values(itinerariesState?.data || {})
      .sort((itineraryA, itineraryB) =>
        dayjs(itineraryA.startDate).isAfter(dayjs(itineraryB.startDate))
          ? 1
          : -1,
      )
      .reduce(
        (obj, itinerary) => {
          const hasFerryItem = itinerary?.itineraryInfo?.reservationItems?.some(
            item =>
              !!item?.ferry?.departures?.length ||
              !!item?.ferry?.landBasedResources?.accommodations?.length ||
              !!item?.ferry?.landBasedResources?.attractions?.length,
          )
          const hasHotelItem = itinerary?.itineraryInfo?.reservationItems?.some(
            item => !!item?.hotel,
          )
          const isCancelledOrUnknown =
            !itinerary.status ||
            cancelledCodes.some(code => code === +itinerary.status)

          if (isCancelledOrUnknown) {
            return {
              ...obj,
              cancelled: [...obj.cancelled, itinerary],
            }
          }

          const startOfToday = dayjs().tz().startOf('day').toDate().getTime()
          const endTime = dayjs
            .tz(itinerary.endDate)
            .startOf('day')
            .toDate()
            .getTime()

          const key = startOfToday <= endTime ? 'upcoming' : 'past'
          if (hasFerryItem || hasHotelItem) {
            return {
              ...obj,
              [key]: [...obj[key], itinerary],
            }
          }
          return {
            ...obj,
          }
        },
        {
          upcoming: [],
          past: [],
          cancelled: [],
          pending,
        },
      )
  },
)

export const selectItinerary = createSelector(
  [selectItineraryState, (state, id) => id],
  (itineraryState, itineraryId) => itineraryState?.data?.[itineraryId] || null,
)
